import { useState } from 'react'
import api from '../../lib/axios'
import toast from 'react-hot-toast'
import clsx from 'clsx'

/**
 * WebhookSetup
 *
 * Handles enabling, disabling, and rotating webhook credentials.
 * Shows provider-specific setup instructions inline.
 *
 * Props:
 *   repositoryId  — number
 *   provider      — 'github' | 'gitlab' | 'bitbucket'
 *   webhookEnabled — bool
 *   webhookUrl    — string | null
 *   onUpdate      — (partialSettings) => void
 */
export default function WebhookSetup({ repositoryId, provider, webhookEnabled, webhookUrl, onUpdate }) {
  const [loading, setLoading]           = useState(false)
  const [secret, setSecret]             = useState(null)   // shown once after enable/rotate
  const [instructions, setInstructions] = useState(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [copiedField, setCopiedField]   = useState(null)

  const copy = async (text, field) => {
    await navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
    toast.success('Copied!')
  }

  const enable = async () => {
    setLoading(true)
    try {
      const { data } = await api.post(`/repositories/${repositoryId}/webhook/enable`)
      setSecret(data.webhook_secret)
      setInstructions(data.instructions)
      setShowInstructions(true)
      onUpdate({ webhook_enabled: true, webhook_url: data.webhook_url })
      toast.success('Webhook enabled! Copy the secret before closing.')
    } catch {
      toast.error('Failed to enable webhook.')
    } finally {
      setLoading(false)
    }
  }

  const disable = async () => {
    if (!confirm('Disable the webhook? Generation will no longer trigger automatically on push.')) return
    setLoading(true)
    try {
      await api.post(`/repositories/${repositoryId}/webhook/disable`)
      setSecret(null)
      setInstructions(null)
      onUpdate({ webhook_enabled: false, webhook_url: null })
      toast.success('Webhook disabled.')
    } catch {
      toast.error('Failed to disable webhook.')
    } finally {
      setLoading(false)
    }
  }

  const rotate = async () => {
    if (!confirm('Rotate the secret? You must update your Git provider webhook configuration with the new secret.')) return
    setLoading(true)
    try {
      const { data } = await api.post(`/repositories/${repositoryId}/webhook/rotate`)
      setSecret(data.webhook_secret)
      setInstructions(data.instructions)
      setShowInstructions(true)
      toast.success('Secret rotated. Update your provider now!')
    } catch {
      toast.error('Failed to rotate secret.')
    } finally {
      setLoading(false)
    }
  }

  const providerLabel = { github: 'GitHub', gitlab: 'GitLab', bitbucket: 'Bitbucket' }[provider] ?? provider

  return (
    <div className="space-y-4">
      {/* Status row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-2.5 h-2.5 rounded-full',
            webhookEnabled ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]' : 'bg-white/20'
          )} />
          <span className="text-sm font-medium text-white">
            Webhook {webhookEnabled ? 'active' : 'inactive'}
          </span>
          {webhookEnabled && (
            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full font-semibold">
              LIVE
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {webhookEnabled && (
            <>
              <button
                type="button"
                disabled={loading}
                onClick={rotate}
                className="text-xs text-white/50 hover:text-amber-400 font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-amber-500/10"
              >
                Rotate secret
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={disable}
                className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-red-500/10"
              >
                Disable
              </button>
            </>
          )}
          {!webhookEnabled && (
            <button
              type="button"
              disabled={loading}
              onClick={enable}
              className="btn-specze-sm text-white"
            >
              {loading ? 'Enabling…' : `Enable ${providerLabel} Webhook`}
            </button>
          )}
        </div>
      </div>

      {/* Webhook URL (when enabled) */}
      {webhookEnabled && webhookUrl && (
        <div>
          <label className="label-specze">Webhook URL</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 input-specze font-mono text-xs text-purple-300 truncate">
              {webhookUrl}
            </code>
            <button
              type="button"
              onClick={() => copy(webhookUrl, 'url')}
              className="flex-shrink-0 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-purple-500/30 text-white/50 hover:text-white transition-all text-xs"
            >
              {copiedField === 'url' ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Secret revealed after enable/rotate */}
      {secret && (
        <div className="p-4 rounded-xl bg-amber-500/8 border border-amber-500/25">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
            <span className="text-xs font-bold text-amber-400">
              Copy this secret now — it will not be shown again
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/30 rounded-lg px-3 py-2 font-mono text-xs text-amber-300 tracking-widest truncate">
              {secret}
            </code>
            <button
              type="button"
              onClick={() => copy(secret, 'secret')}
              className="flex-shrink-0 px-3 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-xs font-semibold transition-all"
            >
              {copiedField === 'secret' ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Setup instructions */}
      {instructions && (
        <div>
          <button
            type="button"
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 font-medium transition-colors"
          >
            <svg className={clsx('w-4 h-4 transition-transform', showInstructions && 'rotate-90')}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
            </svg>
            {showInstructions ? 'Hide' : 'Show'} {providerLabel} setup instructions
          </button>

          {showInstructions && (
            <div className="mt-3 p-4 rounded-xl bg-blue-500/6 border border-blue-500/15 space-y-2.5 animate-fade-in">
              {instructions.steps?.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-400 text-[10px] font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-xs text-white/60 leading-relaxed font-mono">{step}</p>
                </div>
              ))}
              {instructions.docs_url && (
                <a href={instructions.docs_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 mt-1 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  {providerLabel} webhook documentation ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
